import { DateTime } from "luxon";
import { type FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { HOSPITAL_DEMO } from "../../theme/tokens";
import { trackEvent } from "../../utils/analytics";
import { ChartCensus } from "../chartCensus";
import { ChartDateArea } from "../chartDateArea";
import { ChartOverviewModule } from "../chartOverviewModule";
import { ChartScoreArea } from "../chartScoreArea";
import { ChartStatus } from "../chartStatus";
import { DateSlider } from "../dateSlider";
import Style from "./PlanningOverview.module.scss";

export const PlanningOverview: FC = () => {
	const { t } = useTranslation();
	const { hospitalData, getWeekLoadColor, getWeekForceColor, getColorByDifficultyName, getWeekSeasonColor, isHospitalView }: any = useHospitalData();
	const [weekLoad, setWeekLoad] = useState(t("Medium"));
	const [weekSeason, setWeekSession] = useState(t("Medium"));
	const [workForce, setWorkForce] = useState(t("Medium"));
	const [weekLoadText, setWeekLoadText] = useState("");
	const [weekSeasonText, setWeekSeasonText] = useState("");
	const [weekWordkForceText, setWorkForceText] = useState("");
	const [activeBoxIndex, setActiveBoxIndex] = useState(-1);
	const [weekLoadColor, setWeekLoadColor] = useState("");
	const [weekSeasonColor, setWeekSessionColor] = useState("");
	const [workForceColor, setWorkForceColor] = useState("");
	const [hoveredStartDate, setHoveredStartDate] = useState<string>();

	const handleMouseEnter = (
		weekIndex: number,
		weekLoad: string,
		weekSeason: string,
		workForce: string,
		loadText: string,
		seasonText: string,
		wordkForceText: string,
		startDate: string,
	) => {
		setWeekLoad(weekLoad);
		setWeekSession(weekSeason);
		setWorkForce(workForce);
		setWeekLoadText(loadText);
		setWeekSeasonText(seasonText);
		setWorkForceText(wordkForceText);
		setWeekLoadColor(getColorByDifficultyName(weekLoad));
		setWeekSessionColor(getColorByDifficultyName(weekSeason));
		setWorkForceColor(getColorByDifficultyName(workForce));
		setHoveredStartDate(startDate);
		trackEvent("Planning - Week Hovered", { weekIndex });
	};

	useEffect(() => {
		setWeekLoad(hospitalData.weeks[0].weekLoad);
		setWeekSession(hospitalData.weeks[0].weekSeason);
		setWorkForce(hospitalData.weeks[0].workForce);
		setWeekLoadText(hospitalData.weeks[0].loadText);
		setWeekSeasonText(hospitalData.weeks[0].seasonText);
		setWorkForceText(hospitalData.weeks[0].workForceText);
		setWeekLoadColor(getWeekLoadColor(0));
		setWeekSessionColor(getWeekSeasonColor(0));
		setWorkForceColor(getWeekForceColor(0));
	}, [hospitalData.weeks]);

	const groupOfWeeks = () => {
		const modulesPerGroup = 3;
		const groupedModules = [];
		for (let i = 0; i < hospitalData.weeks.length; i += modulesPerGroup) {
			const weekGroup = hospitalData.weeks.slice(i, i + modulesPerGroup);
			const weeklyModule = weekGroup.map((module: any, index: any) => (
				<div
					key={index}
					onMouseEnter={() =>
						handleMouseEnter(
							index,
							module.weekLoad,
							module.weekSeason,
							module.workForce,
							module.loadText,
							module.seasonText,
							module.workForceText,
							module.weeklyShifts[0]?.days[0]?.date,
						)
					}
					onClick={() => {
						setActiveBoxIndex(-1);
						setActiveBoxIndex(module.weeklyShifts[0]?.days[0]?.date);
						handleMouseEnter(
							index,
							module.weekLoad,
							module.weekSeason,
							module.workForce,
							module.loadText,
							module.seasonText,
							module.workForceText,
							module.weeklyShifts[0]?.days[0]?.date,
						);
					}}
				>
					<ChartOverviewModule
						key={index}
						color={getColorByDifficultyName(module.weekDifficulty)}
						status={t(module.weekScore)}
						percentage={module.confidence}
						startDate={module.weeklyShifts[0]?.days[0]?.date}
						endDate={module.weeklyShifts[0]?.days[6]?.date}
						isActive={module.weeklyShifts[0]?.days[0]?.date === activeBoxIndex}
					/>
				</div>
			));

			groupedModules.push(
				<div className={Style.modules} key={i}>
					{weeklyModule}
				</div>,
			);
		}
		return <div>{groupedModules}</div>;
	};
	const hoveredDate = useMemo(() => (hoveredStartDate ? DateTime.fromISO(hoveredStartDate) : DateTime.invalid("no date")), [hoveredStartDate]);

	return (
		<>
			<div className={Style.statusContent}>
				<div className={Style.content}>
					<ChartDateArea day={hoveredDate} color={weekLoadColor} labelEnabled label={t(weekLoadText)} />
					<div style={{ marginTop: "10px", marginBottom: "10px" }}>
						<ChartCensus
							dailyCensusTitle={t("Season")}
							dailyCensusValue={t(weekSeason)}
							dailyCensusColor={weekSeasonColor}
							labelEnabled
							label={t(weekSeasonText)}
						/>
					</div>
					<ChartScoreArea
						patientScoreText={t("Work Force")}
						patientScore={t(workForce)}
						scoreColor={workForceColor}
						labelEnabled
						label={t(weekWordkForceText)}
					/>
				</div>
				<ChartStatus
					status={t("Status")}
					low={t("Low")}
					average={t("Medium")}
					high={t("High")}
					lowColor={isHospitalView ? HOSPITAL_DEMO.NODE_LOW : undefined}
					averageColor={isHospitalView ? HOSPITAL_DEMO.NODE_MEDIUM : undefined}
					highColor={isHospitalView ? HOSPITAL_DEMO.NODE_HIGH : undefined}
				/>
			</div>
			<div className={Style.chartContainer}>
				<DateSlider isFullDates />
				<div style={{ overflowY: "auto", height: "100%" }}>{groupOfWeeks()}</div>
			</div>
		</>
	);
};
